/**
 * useSimpleListItemEditor — Logica condivisa di EmailListEditor + PhoneListEditor.
 *
 * Pattern: lista di item polimorfici (email/phone di Person/Business),
 * ognuno con un value scalare + flag primary. Operazioni:
 *   - update value (string)
 *   - setPrimary (toggle a true; quello vecchio si de-setta server-side)
 *   - remove (con auto-promote del primo rimasto se era primary)
 *   - add (con primary=true se lista era vuota; opzionale validate)
 *
 * Toast success/error gestiti qui dentro (uso `useToast` + `useTranslation`):
 * il consumer riceve solo il flow e i 4 handler. Su update value il throw
 * si propaga al chiamante perche' EditableField entri in modalita' serverError.
 *
 * NB: Address NON usa questo hook — ha API multi-field non riducibile a
 * "value+primary" (vedi AddressListEditor).
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EditableValue } from './EditableField';
import { useToast } from './useToast';

interface UseSimpleListItemEditorOptions<T> {
  items: T[];
  getId: (item: T) => number;
  getPrimary: (item: T) => boolean;
  getValue: (item: T) => string;
  /** Dispatcher concreto: il consumer decide thunk + payload polimorfico. */
  onAdd: (value: string, primary: boolean) => Promise<unknown>;
  onUpdate: (item: T, value: string, primary: boolean) => Promise<unknown>;
  onRemove: (item: T) => Promise<unknown>;
  /** Validazione opzionale per `handleAdd`. Ritorna messaggio errore o null. */
  validate?: (value: string) => string | null;
}

export interface UseSimpleListItemEditorReturn<T> {
  isAdding: boolean;
  setIsAdding: (b: boolean) => void;
  /** Validazione da passare a EditableField (`validate` prop) per update inline. */
  validateValue?: (value: EditableValue) => string | null;
  handleUpdateValue: (item: T, newValue: EditableValue) => Promise<void>;
  handleSetPrimary: (item: T) => Promise<void>;
  handleRemove: (item: T) => Promise<void>;
  handleAdd: (value: string) => Promise<void>;
}

export function useSimpleListItemEditor<T>(
  options: UseSimpleListItemEditorOptions<T>,
): UseSimpleListItemEditorReturn<T> {
  const { items, getId, getPrimary, getValue, onAdd, onUpdate, onRemove, validate } = options;
  const { t } = useTranslation();
  const toast = useToast();
  const [isAdding, setIsAdding] = useState(false);

  // EditableField richiede `validate(value: EditableValue)`. Wrap del
  // validate string-based con la coercion sicura. Esposto solo se il
  // chiamante ha passato un validate.
  const validateValue = validate
    ? (value: EditableValue): string | null => {
        const str = String(value ?? '').trim();
        if (!str) return null;
        return validate(str);
      }
    : undefined;

  const handleUpdateValue = useCallback(
    async (item: T, newValue: EditableValue) => {
      const value = String(newValue ?? '').trim();
      try {
        await onUpdate(item, value, getPrimary(item));
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    },
    [onUpdate, getPrimary, toast, t],
  );

  const handleSetPrimary = useCallback(
    async (item: T) => {
      if (getPrimary(item)) return;
      try {
        await onUpdate(item, getValue(item), true);
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    },
    [onUpdate, getPrimary, getValue, toast, t],
  );

  const handleRemove = useCallback(
    async (item: T) => {
      const wasPrimary = getPrimary(item);
      const removedId = getId(item);
      try {
        await onRemove(item);
        // Auto-promote del primo rimasto se quello eliminato era primary
        // (mantiene l'invariante "esiste sempre un primary se ci sono item").
        if (wasPrimary) {
          const remaining = items.filter((i) => getId(i) !== removedId);
          if (remaining.length > 0) {
            await onUpdate(remaining[0], getValue(remaining[0]), true);
          }
        }
        toast({ severity: 'success', message: t('common.saved') });
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    },
    [items, onRemove, onUpdate, getId, getPrimary, getValue, toast, t],
  );

  const handleAdd = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) {
        setIsAdding(false);
        return;
      }
      if (validate) {
        const errMsg = validate(trimmed);
        if (errMsg) {
          toast({ severity: 'error', message: errMsg });
          return;
        }
      }
      try {
        await onAdd(trimmed, items.length === 0);
        toast({ severity: 'success', message: t('common.saved') });
        setIsAdding(false);
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      }
    },
    [items.length, onAdd, validate, toast, t],
  );

  return {
    isAdding,
    setIsAdding,
    validateValue,
    handleUpdateValue,
    handleSetPrimary,
    handleRemove,
    handleAdd,
  };
}
